/**
 * Client Onboarding Status Hook
 * 
 * Aggregates completion status from client profile and organization profile
 * to provide a unified onboarding status for client users.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/lib/auth';

export interface ClientOnboardingStatus {
  needsOnboarding: boolean;
  profileCompletionPercentage: number;
  userProfileComplete: boolean;
  companyProfileComplete: boolean;
  missingItems: string[];
  isLoading: boolean;
}

export const useClientOnboardingStatus = () => {
  const { user, organization } = useAuth();

  return useQuery<ClientOnboardingStatus>({
    queryKey: ['client-onboarding-status', user?.id, organization?.id],
    queryFn: async () => {
      if (!user?.id || !organization?.id) {
        return {
          needsOnboarding: true,
          profileCompletionPercentage: 0,
          userProfileComplete: false,
          companyProfileComplete: false,
          missingItems: ['Complete your profile', 'Add company information'],
          isLoading: false,
        };
      }

      const missingItems: string[] = [];

      // Fetch client profile completion
      const { data: clientProfile } = await supabase
        .from('client_profiles')
        .select('id, first_name, last_name, phone_number, job_title, typical_budget_range, procurement_frequency, decision_authority_level')
        .eq('user_id', user.id)
        .maybeSingle();

      // Fetch organization profile completion
      const { data: orgProfile } = await supabase
        .from('organization_profiles')
        .select('id, industry, company_size, description, annual_revenue_range, street_address, city, state, zip_code, accounts_payable_email')
        .eq('organization_id', organization.id)
        .maybeSingle();

      // Calculate user profile completion
      let userProfileComplete = false;
      if (clientProfile) {
        const basicInfoComplete = !!(
          clientProfile.first_name &&
          clientProfile.last_name &&
          clientProfile.phone_number &&
          clientProfile.job_title
        );

        userProfileComplete = basicInfoComplete;

        if (!basicInfoComplete) {
          missingItems.push('Complete your basic profile information');
        }
      } else {
        missingItems.push('Complete your user profile');
      }

      // Calculate company profile completion
      let companyProfileComplete = false;
      if (orgProfile) {
        const basicInfoComplete = !!(
          orgProfile.industry &&
          orgProfile.company_size &&
          orgProfile.description
        );

        const businessInfoComplete = !!(
          orgProfile.street_address &&
          orgProfile.city &&
          orgProfile.state &&
          orgProfile.zip_code
        );

        companyProfileComplete = basicInfoComplete && businessInfoComplete;

        if (!basicInfoComplete) {
          missingItems.push('Add company basic information');
        }
        if (!businessInfoComplete) {
          missingItems.push('Add company business details');
        }
      } else {
        missingItems.push('Set up your company profile');
      }

      // Calculate combined completion percentage
      const userScore = userProfileComplete ? 50 : (clientProfile ? 25 : 0);
      const companyScore = companyProfileComplete ? 50 : (orgProfile ? 25 : 0);
      const profileCompletionPercentage = userScore + companyScore;

      const needsOnboarding = !userProfileComplete || !companyProfileComplete;

      return {
        needsOnboarding,
        profileCompletionPercentage,
        userProfileComplete,
        companyProfileComplete,
        missingItems,
        isLoading: false,
      };
    },
    enabled: !!user?.id && !!organization?.id,
  });
};
